import { createContext, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "vb_lang";
const DEFAULT_LANG = "hu";
const SUPPORTED = ["en", "hu"];

export const translations = {
  en: {
    brand: {
      studio: "Digit-Studio",
      tagline: "Preserve every memory before the tape fades.",
    },
    nav: {
      services: "Services",
      process: "How it works",
      pricing: "Packages",
      contact: "Contact",
      callNow: "Call Now",
    },
    hero: {
      eyebrow: "Memory Restoration Studio",
      headlineA: "The tapes won't last",
      headlineB: "forever.",
      headlineC: "Your memories will.",
      subhead:
        "We carefully digitize VHS, audio cassettes, film reels and old photographs into crisp digital files — so the moments your family made stay alive for the next generation.",
      callPrefix: "Call",
      seePricing: "Get in touch",
      stats: [
        { k: "1000+", v: "Tapes restored" },
        { k: "5–7 days", v: "Turnaround" },
        { k: "100%", v: "Tracked & insured" },
      ],
    },
    services: {
      eyebrow: "01 — What we restore",
      titleA: "Every format. Every era.",
      titleB: "Handled like a heirloom.",
      note: "Tapes degrade about 20% over a decade. We work fast, but never rushed — your originals always come back to you.",
      seePricing: "Get in touch",
      items: {
        vhs: {
          title: "VHS & Video Tapes",
          blurb:
            "VHS, VHS-C, S-VHS, Hi8, Video8, Digital8, MiniDV and more. Captured at the highest fidelity each format allows.",
        },
        audio: {
          title: "Audio Cassettes",
          blurb:
            "Compact cassettes, micro-cassettes and reel-to-reel — cleaned and digitized as lossless WAV + MP3.",
        },
        film: {
          title: "Film Reels & Camcorders",
          blurb: "8mm, Super 8 and 16mm reels — scanned frame-by-frame in HD.",
        },
        photos: {
          title: "Photographs, Slides & Negatives",
          blurb:
            "Loose prints, albums, 35mm slides and negatives — restored, color-corrected and indexed.",
        },
      },
    },
    process: {
      eyebrow: "02 — The process",
      titleA: "Three careful steps.",
      titleB: "Zero compromise.",
      steps: [
        {
          n: "01",
          title: "Send it or drop it off",
          body: "Insured prepaid shipping or studio drop-off. Every tape is logged on arrival with a photograph.",
        },
        {
          n: "02",
          title: "We restore & digitize",
          body: "Professional decks, calibrated transports, gentle cleaning. We capture once, the right way.",
        },
        {
          n: "03",
          title: "Receive your memories",
          body: "Originals returned untouched. Digital files delivered via private cloud, USB or hard drive.",
        },
      ],
    },
    pricing: {
      eyebrow: "03 — Packages",
      titleA: "Every collection is unique.",
      titleB: "Let's talk about yours.",
      note: "Every project is different. Call us — we'll give you a custom quote in under 5 minutes.",
      mostChosen: "Most chosen",
      reserve: "Call for a quote",
      redirecting: "Calling…",
      callPrefix: "Call",
      packages: {
        single_cassette: {
          name: "Small Batch",
          description: "Digitize 1–3 tapes in any format.",
          features: [
            "1–3 tapes, any format",
            "MP4 digital video",
            "Private download link",
            "5–7 day turnaround",
          ],
        },
        family_bundle: {
          name: "Family Bundle",
          description: "Digitize up to 10 tapes of mixed format.",
          features: [
            "Up to 10 tapes, mixed formats",
            "High-quality MP4 video",
            "USB drive included",
            "Color & audio cleanup",
            "Priority queue",
          ],
        },
        legacy_archive: {
          name: "Legacy Archive",
          description: "Digitize up to 15 tapes + photo box (200 photos).",
          features: [
            "Up to 15 tapes",
            "200 photos scanned (600 dpi)",
            "Engraved external SSD",
            "Pro restoration pass",
            "White-glove return shipping",
          ],
        },
      },
      errors: {
        cantStart: "Could not start checkout. Please try again.",
        cantInit: "Payment couldn't be initialized. Please call us instead.",
      },
    },
    testimonials: {
      eyebrow: "04 — In their words",
      titleA: "Families have trusted us",
      titleB: "with their most fragile memories.",
      ratingLabel: "Google rating",
      ratingsCount: "verified reviews",
      writeReview: "Write a Google review",
      verifiedBadge: "Verified Google review",
      items: [
        {
          q: "I'd given up on these tapes — they came back so clear my mom cried watching her father's wedding. Worth every penny.",
          a: "Eleanor R.",
          role: "Brooklyn, NY",
        },
        {
          q: "47 cassettes of my dad's radio show, mailed in. Returned in three weeks, restored, labeled, archived. Real professionals.",
          a: "Marcus T.",
          role: "Austin, TX",
        },
        {
          q: "They treated our family reels like they were their own. The color correction on the Super 8 footage is unreal.",
          a: "Priya & James",
          role: "San Diego, CA",
        },
      ],
    },
    faq: {
      eyebrow: "05 — Answered",
      titleA: "Questions we",
      titleB: "hear every day.",
      items: [
        {
          q: "Which formats can you digitize?",
          a: "VHS, VHS-C, S-VHS, Hi8, Video8, Digital8, MiniDV, Betamax, U-matic, audio compact cassette, micro-cassette, reel-to-reel, 8mm/Super 8/16mm film, 35mm slides, negatives and prints. If you have something exotic — call us, we probably handle it.",
        },
        {
          q: "How long does it take?",
          a: "Standard turnaround is 5–7 business days from arrival at the studio. The Family Bundle is prioritized. Larger archives (50+ items) are scoped individually.",
        },
        {
          q: "How do I receive the digital files?",
          a: "A private cloud download link by default. We can also deliver on a USB drive, external SSD, or your supplied storage. The Family Bundle and Legacy Archive include physical media.",
        },
        {
          q: "Can you repair damaged tapes?",
          a: "Most snapped tape, sticky shed and warped shells we can repair in-house before capture. We'll always call first if repair work is needed beyond the package scope.",
        },
        {
          q: "Do you offer in-person drop-off?",
          a: "Yes — by appointment. Call us to schedule. We can also send a prepaid, insured shipping label for remote customers.",
        },
      ],
    },
    contact: {
      eyebrow: "06 — Get in touch",
      titleA: "Let's talk about",
      titleB: "your memories.",
      note: "Calling is fastest — we usually pick up on the second ring. Prefer to write? Drop us a note and we'll get back within one business day.",
      labels: {
        call: "Call",
        email: "Email",
        hours: "Hours",
        name: "Name",
        nameP: "Your full name",
        emailF: "Email",
        emailP: "you@example.com",
        phone: "Phone (optional)",
        phoneP: "+1 (555) 000-0000",
        items: "Number of items",
        itemsP: "e.g. 12 VHS + 30 photos",
        message: "Tell us about your collection",
        messageP: "What formats, how many tapes, any urgency…",
      },
      submit: "Send message",
      sending: "Sending…",
      privacy: "We never share your information. You'll hear back within one business day.",
      errors: {
        missing: "Please add your name, email and a short message.",
        failed: "Something went wrong. Please call us directly.",
      },
      success: "Got it — we'll be in touch within one business day.",
    },
    footer: {
      reach: "Reach us",
      studio: "Studio",
      rights: "All rights reserved.",
      crafted: "Crafted for memories that matter",
    },
    success: {
      back: "Back to",
      checking: "Confirming your payment…",
      checkingNote: "This usually takes a few seconds.",
      paidTitle: "Your slot is reserved.",
      paidNote:
        "Thank you — we've received your payment. Our team will reach out within one business day to coordinate pickup or shipping for your",
      paidFallback: "package",
      backHome: "Back to home",
      errorTitle: "We couldn't confirm your payment.",
      errorNote: "If you were charged, please call us — we'll sort it out instantly.",
      missingTitle: "No payment session found.",
      returnHome: "Return home",
      callPrefix: "Call",
    },
  },

  hu: {
    brand: {
      studio: "Digit-Studio",
      tagline: "Mentsük meg együtt a legértékesebb pillanatait, mielőtt a szalag elveszti őket.",
    },
    nav: {
      services: "Szolgáltatások",
      process: "Folyamat",
      pricing: "Csomagok",
      contact: "Kapcsolat",
      callNow: "Hívjon most",
    },
    hero: {
      eyebrow: "Emlékmegőrző Stúdió · Budapest",
      headlineA: "A szalag",
      headlineB: "elhalványul.",
      headlineC: "Az emlék nem.",
      subhead:
        "Gondosan digitalizáljuk az Ön VHS-kazettáit, hangfelvételeit, filmtekercseit és régi fényképeit tiszta digitális fájllá — hogy a család közös pillanatai egy életen át megmaradjanak.",
      callPrefix: "Hívás:",
      seePricing: "Kapcsolatfelvétel",
      stats: [
        { k: "1000+", v: "Mentett szalag" },
        { k: "5–7 nap", v: "Átfutási idő" },
        { k: "100%", v: "Biztosítva kezelve" },
      ],
    },
    services: {
      eyebrow: "01 — Amit helyreállítunk",
      titleA: "Minden formátum. Minden korszak.",
      titleB: "Családi ereklyeként kezelve.",
      note: "A szalagok minőségüknek közel 20%-át veszítik el évtizedenként. Mi gyorsan dolgozunk, de soha nem kapkodva — az eredetiket mindig sértetlenül kapja vissza.",
      seePricing: "Kapcsolatfelvétel",
      items: {
        vhs: {
          title: "VHS és Videószalagok",
          blurb:
            "VHS, VHS-C, S-VHS, Hi8, Video8, Digital8, MiniDV és további formátumok. Az adott formátum által engedett legjobb minőségben.",
        },
        audio: {
          title: "Hangkazetták",
          blurb:
            "Normál és mikro hangkazetták, valamint orsós szalagok — gondos tisztítás után, veszteségmentes WAV + MP3 formátumban.",
        },
        film: {
          title: "Filmtekercsek és Régi Kamerák",
          blurb: "8 mm, Super 8 és 16 mm filmek — képkockánkénti HD szkennelés, kíméletes színhelyesbítéssel.",
        },
        photos: {
          title: "Fényképek, Diák és Negatívok",
          blurb:
            "Különálló képek, albumok, 35 mm-es diák és negatívok — restaurálva, színhelyesbítve és indexelve.",
        },
      },
    },
    process: {
      eyebrow: "02 — A folyamat",
      titleA: "Négy gondos lépés.",
      titleB: "Nulla kompromisszum.",
      steps: [
        {
          n: "01",
          title: "Küldje el vagy hozza be személyesen",
          body: "Biztosított, előre fizetett szállítást vállalunk, vagy várjuk Önt a stúdióban. Érkezéskor minden szalagot fényképpel naplózunk.",
        },
        {
          n: "02",
          title: "Helyreállítjuk és digitalizáljuk",
          body: "Profi lejátszók, kalibrált szalagvezetés, kíméletes tisztítás. Egyszer rögzítünk — a megfelelő módon.",
        },
        {
          n: "03",
          title: "Visszakapja az emlékeit",
          body: "Az eredetiket érintetlenül szolgáltatjuk vissza. A digitális fájlokat privát felhőn, USB-n vagy merevlemezen biztosítjuk.",
        },
      ],
    },
    pricing: {
      eyebrow: "03 — Csomagok",
      titleA: "Minden gyűjtemény egyedi.",
      titleB: "Beszéljünk az Önéről.",
      note: "Minden megbízás más — a szalagok számától, állapotától és formátumától függ. Hívjon, és 5 percen belül egyedi ajánlatot adunk.",
      mostChosen: "Legkedveltebb",
      reserve: "Hívjon árajánlatért",
      redirecting: "Hívás…",
      callPrefix: "Hívás:",
      packages: {
        single_cassette: {
          name: "Kis csomag",
          description: "1–3 szalag digitalizálása, bármilyen formátumban.",
          features: [
            "1–3 szalag, bármely formátum",
            "MP4 digitális videó",
            "Privát letöltési link",
            "5–7 napos átfutás",
          ],
        },
        family_bundle: {
          name: "Családi csomag",
          description: "Akár 10 vegyes formátumú szalag digitalizálása.",
          features: [
            "Akár 10 szalag, vegyes formátumokban",
            "Magas minőségű MP4 videó",
            "USB-meghajtó mellékelve",
            "Szín- és hangtisztítás",
            "Elsőbbségi sor",
          ],
        },
        legacy_archive: {
          name: "Hagyaték archívum",
          description: "Akár 15 szalag + fényképdoboz (200 db) digitalizálása.",
          features: [
            "Akár 15 szalag",
            "200 fénykép szkennelve (600 dpi)",
            "Vésett külső SSD",
            "Profi restaurálás",
            "Fehérkesztyűs visszaszállítás",
          ],
        },
      },
      errors: {
        cantStart: "A fizetést nem sikerült elindítani. Kérjük, próbálja meg újra.",
        cantInit: "A fizetés nem indítható el. Kérjük, hívjon minket helyette.",
      },
    },
    testimonials: {
      eyebrow: "04 — Az ő szavaikkal",
      titleA: "Családok bennünket bíznak meg",
      titleB: "a legtörékenyebb emlékeikkel.",
      ratingLabel: "Google értékelés",
      ratingsCount: "hiteles vélemény",
      writeReview: "Értékelje a Google-on",
      verifiedBadge: "Hiteles Google vélemény",
      items: [
        {
          q: "Már lemondtam ezekről a szalagokról. Olyan tisztán jöttek vissza, hogy édesanyám sírt, amikor az apja esküvőjét nézte. Megérte minden fillért.",
          a: "Eleonóra R.",
          role: "Budapest",
        },
        {
          q: "Édesapám 47 rádióműsoros kazettáját küldtem be. Három hét múlva, restaurálva, címkézve és archiválva érkeztek vissza. Profi munka.",
          a: "Tamás M.",
          role: "Szeged",
        },
        {
          q: "Úgy bántak a családi filmtekercseinkkel, mintha sajátjaik lennének. A Super 8-as anyag színhelyesbítése elképesztő lett.",
          a: "Petra és János",
          role: "Pécs",
        },
      ],
    },
    faq: {
      eyebrow: "05 — Válaszok",
      titleA: "Amit nap mint nap",
      titleB: "megkérdeznek tőlünk.",
      items: [
        {
          q: "Milyen formátumokat tudnak digitalizálni?",
          a: "VHS, VHS-C, S-VHS, Hi8, Video8, Digital8, MiniDV, Betamax, U-matic, normál hangkazetta, mikrokazetta, orsós szalag, 8 mm / Super 8 / 16 mm film, 35 mm-es diák, negatívok és nyomatok. Ha valami különlegese van — hívjon, valószínűleg azt is kezeljük.",
        },
        {
          q: "Mennyi időt vesz igénybe a folyamat?",
          a: "A standard átfutás a stúdióba érkezéstől számított 5–7 munkanap.",
        },
        {
          q: "Hogyan kapom meg a digitális fájlokat?",
          a: "Alapból privát felhős letöltési linken. Igény szerint USB-meghajtón, külső SSD-n vagy az Ön által biztosított adathordozón is szállítjuk. A Családi csomag és a Hagyaték archívum már tartalmaz fizikai adathordozót.",
        },
        {
          q: "Tudják javítani a sérült szalagokat?",
          a: "A legtöbb elszakadt szalagot, ragadós-szalag hibát és deformálódott tokot házon belül javítjuk a rögzítés előtt.",
        },
        {
          q: "Lehetséges személyes leadás Budapesten?",
          a: "Igen — előzetes egyeztetéssel. Hívjon az időpontfoglaláshoz. Távoli ügyfeleknek előre fizetett, biztosított szállítási címkét is küldünk.",
        },
      ],
    },
    contact: {
      eyebrow: "06 — Lépjen kapcsolatba",
      titleA: "Beszéljünk az",
      titleB: "Ön emlékeiről.",
      note: "Telefonon a leggyorsabb — általában a második csengetésnél felvesszük. Inkább írna? Hagyjon egy üzenetet, és egy munkanapon belül válaszolunk.",
      labels: {
        call: "Hívás",
        email: "E-mail",
        hours: "Nyitvatartás",
        name: "Név",
        nameP: "Az Ön teljes neve",
        emailF: "E-mail",
        emailP: "on@pelda.hu",
        phone: "Telefon (opcionális)",
        phoneP: "+36 30 000 0000",
        items: "Tételek száma",
        itemsP: "pl. 12 VHS + 30 fénykép",
        message: "Mondjon pár szót a gyűjteményéről",
        messageP: "Milyen formátumok, hány szalag, sürgősség…",
      },
      submit: "Üzenet küldése",
      sending: "Küldés…",
      privacy: "Az Ön adatait soha nem adjuk ki harmadik félnek. Egy munkanapon belül válaszolunk.",
      errors: {
        missing: "Kérjük, adja meg a nevét, e-mail címét és egy rövid üzenetet.",
        failed: "Hiba történt. Kérjük, hívjon minket közvetlenül.",
      },
      success: "Köszönjük — egy munkanapon belül felvesszük Önnel a kapcsolatot.",
    },
    footer: {
      reach: "Elérhetőség",
      studio: "Stúdió",
      rights: "Minden jog fenntartva.",
      crafted: "A fontos emlékekért készítve",
    },
    success: {
      back: "Vissza ide:",
      checking: "Fizetés megerősítése…",
      checkingNote: "Ez általában néhány másodperc.",
      paidTitle: "Az időpontját lefoglaltuk.",
      paidNote:
        "Köszönjük — a fizetést megkaptuk. Egy munkanapon belül felvesszük Önnel a kapcsolatot az átvétel vagy szállítás egyeztetése miatt. Csomag:",
      paidFallback: "csomag",
      backHome: "Vissza a főoldalra",
      errorTitle: "Nem tudtuk megerősíteni a fizetést.",
      errorNote: "Ha mégis levonták az összeget, kérjük hívjon — azonnal rendezzük.",
      missingTitle: "Nem található fizetési munkamenet.",
      returnHome: "Vissza a főoldalra",
      callPrefix: "Hívás:",
    },
  },
};

const LangContext = createContext({ lang: DEFAULT_LANG, setLang: () => {}, t: translations[DEFAULT_LANG] });

function detectInitialLang() {
  if (typeof window === "undefined") return DEFAULT_LANG;
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved && SUPPORTED.includes(saved)) return saved;
  } catch (_) {}
  // Default to Hungarian for all new visitors (user choice).
  // Visitors can switch to EN via the header toggle, which then persists.
  return "hu";
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(DEFAULT_LANG);

  useEffect(() => {
    const initial = detectInitialLang();
    setLangState(initial);
    if (typeof document !== "undefined") {
      document.documentElement.lang = initial;
    }
  }, []);

  const setLang = (next) => {
    if (!SUPPORTED.includes(next)) return;
    setLangState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch (_) {}
    if (typeof document !== "undefined") {
      document.documentElement.lang = next;
    }
  };

  const value = useMemo(() => ({ lang, setLang, t: translations[lang] || translations[DEFAULT_LANG] }), [lang]);

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export function useT() {
  return useContext(LangContext).t;
}

export function useLang() {
  const { lang, setLang } = useContext(LangContext);
  return { lang, setLang, supported: SUPPORTED };
}
